import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "group/button inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/80",
        outline:
          "border-border bg-background shadow-xs hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-[color-mix(in_oklch,var(--secondary),var(--foreground)_5%)] aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
        ghost:
          "hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50",
        destructive:
          "bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        // Padding is public-first and admin density is opt-in, not the reverse.
        // This primitive is shared: `src/app/(public)/**` renders <Button> 125
        // times — 73 default, 36 lg, 12 sm, 4 icon — across tuscancove.com,
        // tenant public domains, where the brand contract,
        // not the Create preset, governs. Taking Create's dense px-2.5 as the
        // BASE narrowed 65 of them (46 default + 19 lg); the others happened to
        // pass their own px-* or w-full and were spared, which is why the
        // damage was patchy rather than obvious.
        //
        // Create's density now arrives only inside an ops surface, via
        // `data-ops-shell` on <html> (set by the pre-paint script in
        // src/app/layout.tsx; its prefix list is pinned against the
        // admin-theme.css importer set by tests/ops-shell-route-corpus.test.ts).
        // Keyed on that attribute rather than `.admin-theme` on purpose:
        // portaled dialogs, sheets, selects and toasts mount outside the
        // .admin-theme wrapper but still under <html>, so a .admin-theme key
        // would leave every button inside an admin dialog at public padding.
        //
        // FOOT-GUN: the gated utility sits later in the stylesheet at equal
        // specificity (`:where()` contributes none), so inside the ops shell it
        // beats any plain `px-*` a call site passes — twMerge strips the base
        // px-4 but has no reason to strip the gated class. To change padding on
        // an admin button, pass `in-data-[ops-shell]:px-*`, not `px-*`.
        //
        // NOT restored: origin/main's `has-[>svg]:px-3` / `has-[>svg]:px-4` icon
        // compensation, which the Create scale replaced with `has-data-[icon=…]`
        // hooks that public call sites never set. Public icon CTAs therefore sit
        // 4-8px WIDER than the pre-branch design rather than tighter. Left that
        // way deliberately: an ungated `has-*` variant would tie with the
        // ops-shell gate at equal specificity, making admin padding depend on
        // emit order. Generous beats non-deterministic.
        default:
          "h-9 gap-1.5 px-4 in-data-[ops-shell]:px-2.5 in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        xs: "h-6 gap-1 rounded-[min(var(--radius-md),8px)] px-2 text-xs in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        // Same split as `default`/`lg`, at the smaller scale: origin/main had
        // `px-3` and 12 public call sites still render it (order/layout,
        // order/checkout, order/confirmation, blog/[slug], events/[slug]).
        sm: "h-8 gap-1 rounded-[min(var(--radius-md),10px)] px-3 in-data-[ops-shell]:px-2.5 in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5",
        // Same split as `default`. lg is the public hero/CTA size (36 public call
        // sites vs 4 in admin), so px-6 is the value that matters here.
        lg: "h-10 gap-1.5 px-6 in-data-[ops-shell]:px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        icon: "size-9",
        "icon-xs":
          "size-6 rounded-[min(var(--radius-md),8px)] in-data-[slot=button-group]:rounded-md [&_svg:not([class*='size-'])]:size-3",
        "icon-sm":
          "size-8 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-md",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
